import type { StrategyListItem } from '../../../../../bridge/types/strategy.ts';

import type { OrbPalette } from './orbRenderer';

// The colour an orb arrives FROM when it joins the dock: its own silhouette with the hue
// drained, which then floods to the tier colour over 360ms. A neutral start makes the arrival
// read as the strategy coming alive rather than an element popping into place.
export const ARRIVAL_ORB_PALETTE: OrbPalette = {
	h: 0,
	s: 0,
	lFar: 74,
	lNear: 34,
};

// Where a strategy sits in its run-level lifecycle. `status` is checked FIRST because a
// strategy activated before the lifecycle stamps existed carries no `activated_at` — status
// is the durable signal, the timestamps only distinguish stopped-once from never-started.
export type StrategyGroupKey = 'active' | 'paused' | 'generating' | 'new';

// The four states a strategy can be in. `status` is checked before the stamps because a
// strategy activated before the lifecycle stamps existed carries no `activated_at`.
export function strategyGroupOf(item: StrategyListItem): StrategyGroupKey {
	if (item.status === 'generating') return 'generating';
	if (item.status === 'active') return 'active';
	if (item.deactivated_at) return 'paused';
	return 'new';
}

// Rows with no strategy_generation_batch_id share ONE bucket rather than becoming singletons: legacy runs
// predate batching and a real 3-run batch that lost the field must not split into three.
const NO_BATCH = '\u0000no-batch';

function batchKeyOf(item: StrategyListItem): string {
	return item.strategy_generation_batch_id ?? NO_BATCH;
}

// Milliseconds for ordering. An absent/unparseable stamp sorts last instead of poisoning
// every comparison with NaN (which would silently hide the row forever).
function startedMs(item: StrategyListItem): number {
	const t = Date.parse(item.started_at ?? '');
	return Number.isNaN(t) ? -Infinity : t;
}

// The most recently generated batch. Grouped on strategy_generation_batch_id and compared on the newest
// `started_at` in each group — never on array order, because the list arrives sorted by
// Cosmos `_ts` (last-modified), so merely activating a strategy would reorder it.
export function latestBatchKey(items: StrategyListItem[]): string | null {
	let bestKey: string | null = null;
	let bestMs = -Infinity;
	for (const item of items) {
		const key = batchKeyOf(item);
		const ms = startedMs(item);
		// Ties resolve on the batch key so the winner is stable across re-renders. The
		// first row always wins outright, so a list whose stamps are ALL unparseable still
		// names a real batch instead of hiding every row.
		if (bestKey === null || ms > bestMs || (ms === bestMs && key > bestKey)) {
			bestKey = key;
			bestMs = ms;
		}
	}
	return bestKey;
}

// What the dock shows. A strategy earns its place by being live, by having been live, or
// by belonging to the newest batch and still awaiting a decision — so generating again
// clears away the previous batch's untouched suggestions without ever hiding work the
// owner acted on.
export function visibleDockRows(items: StrategyListItem[]): StrategyListItem[] {
	const latest = latestBatchKey(items);
	return items.filter((item) => {
		const group = strategyGroupOf(item);
		// Active and paused strategies always keep their place; the ones nobody has started
		// only survive while they are the newest batch.
		if (group === 'active' || group === 'paused') return true;
		return batchKeyOf(item) === latest;
	});
}

// The rows that get an orb in the dock bar: ONLY the strategies actually running.
//
// The bar is the always-visible surface — an orb there is the app saying "this is live right
// now". Showing one per generated strategy made three untouched ideas look like three running
// strategies, which is the opposite of the truth. With nothing active the bar keeps only its
// empty slot, and that slot invites a FIRST strategy only when the owner has none: "Create
// Strategy" is a lie to an owner who already has some. The menu still lists everything the
// owner has; the bar is the live indicator, not a launcher.
export function dockIconRows(items: StrategyListItem[]): StrategyListItem[] {
	// An open request does not earn an orb. The bar says only what is live now.
	// A paused strategy leaves the bar, and the dock menu still lists it.
	return items.filter((item) => item.status === 'active');
}

export interface StrategyGroup {
	key: StrategyGroupKey;
	label: string;
	items: StrategyListItem[];
}

// Reading order: live work first, then what was paused, then what is still being built, then
// what has never been started.
const GROUP_ORDER: { key: StrategyGroupKey; label: string }[] = [
	{ key: 'active', label: 'Active' },
	{ key: 'paused', label: 'Paused' },
	{ key: 'generating', label: 'Generating' },
	{ key: 'new', label: 'New' },
];

function newestFirst(a: StrategyListItem, b: StrategyListItem): number {
	return (
		Date.parse(b.completed_at ?? b.started_at ?? '') -
			Date.parse(a.completed_at ?? a.started_at ?? '') || 0
	);
}

// Group the curated rows for display. Empty groups are dropped, so a menu with one running
// strategy shows exactly one heading instead of empty scaffolding.
export function groupedDockRows(items: StrategyListItem[]): StrategyGroup[] {
	return GROUP_ORDER.map(({ key, label }) => ({
		key,
		label,
		items: items.filter((i) => strategyGroupOf(i) === key).sort(newestFirst),
	})).filter((g) => g.items.length > 0);
}

export type DockIconStatus =
	| { kind: 'generating' }
	| { kind: 'failed' }
	| { kind: 'attention' }
	| { kind: 'standing' }
	| { kind: 'active' }
	| { kind: 'ready' };

// Per-icon status — the states the orb motion distinguishes: still generating, a terminal
// failure (retryable), running its daily loop, or a finished plan awaiting a decision.
// There is no user-facing "to review" state: a strategy can't be edited, so residual
// validation flags are for logging only (the CTA button carries all the state a reader
// acts on).
// The kinds that ASK the owner for something. Mirrors FLAG_KINDS_THAT_ASK in
// services/py/shared/models/strategies.py. An unknown kind counts as information, so a kind we
// have not shipped yet can never pin a card on screen with no way to remove it.
const FLAG_KINDS_THAT_ASK = new Set([
	'manual_metric_needed',
	'approval_requested',
	'stalled_owner_steps',
	// Go-live raises this for a step only the owner can do, and only an answer stamps
	// `resolved_at`. Reading it must not take it off the dock.
	'owner_action_needed',
	// A built campaign waiting to be started. Opening its setup thread is not starting it, so
	// the request stands until the campaign runs.
	'campaign_setup_needed',
]);
// `campaign_setup_failed` is deliberately ABSENT. It reports a build Bridgeway could not finish,
// and the owner cannot fix that from the dock. Nothing stamps `resolved_at` on the kind, so
// listing it as an ask pulsed the orb for ever. It is information: the next activation rebuilds
// the campaign. Python agrees at `shared/models/strategies.py:308`, and the two predicates must
// answer the same question or the owner reaches a request through one surface and not another.

type StrategyFlag = NonNullable<StrategyListItem['attention_flags']>[number];

// Whether one request still needs the owner. THE single predicate: the orb's state, the route
// into the notification list, the dock label and the card must all ask this same question, or
// the owner reaches a request through one surface and not another.
//
// An ask needs the owner until the answer arrives. Reading it is not answering it, so a read
// request stays. Information needs the owner until they read it, and then it goes.
export function flagNeedsOwner(flag: StrategyFlag): boolean {
	return FLAG_KINDS_THAT_ASK.has(flag.kind)
		? !flag.resolved_at
		: !flag.acknowledged_at;
}

// The requests the strategy is still waiting on.
export function openFlags(item: StrategyListItem) {
	return (item.attention_flags ?? []).filter(flagNeedsOwner);
}

// How a waiting count is spoken, everywhere it is spoken. The dock orb and the strategy card both
// say this, so the phrase lives here: two copies of one sentence drift, and a reader who meets two
// wordings reads two different ideas.
export function requestsWaitingLabel(count: number): string {
	return count === 1 ? '1 request waiting' : `${count} requests waiting`;
}

export const OWNER_ASK_FLAG_KIND = 'owner_action_needed';

export const CAMPAIGN_SETUP_FLAG_KIND = 'campaign_setup_needed';

// The kinds that go calm once the owner has read them, while still waiting on work only they
// can do. A campaign waiting to be started belongs here for the same reason an owner ask does.
const KINDS_THAT_STAND = new Set([
	OWNER_ASK_FLAG_KIND,
	CAMPAIGN_SETUP_FLAG_KIND,
]);

// A flag the owner has read, but which still waits on work only they can do. It keeps its row
// and a calm mark, and it stops pulsing. A reminder that pulses for a week reads as a fault.
export function standingFlags(item: StrategyListItem) {
	return (item.attention_flags ?? []).filter(
		(f) => KINDS_THAT_STAND.has(f.kind) && f.acknowledged_at && !f.resolved_at,
	);
}

// An answered ask keeps its stored record, so the history stays, but it stops taking room in
// the list. Without this the standing reminder drowns in rows nobody has to act on.
export function isRetired(flag: {
	kind?: string;
	resolved_at?: string | null;
}) {
	return flag.kind === OWNER_ASK_FLAG_KIND && Boolean(flag.resolved_at);
}

// Every part of this strategy the owner still owes, read or unread. The card lists them, so
// the owner has a route to the ask that does not depend on the notification being unseen.
export function openOwnerAsks(item: StrategyListItem) {
	return flagsNewestFirst(item.attention_flags).filter(
		(f) => f.kind === OWNER_ASK_FLAG_KIND && f.action_id,
	);
}

export function dockIconStatus(
	item: StrategyListItem,
	// How many proposals this strategy is waiting for an answer on. A proposal lives on the
	// strategy CARD, which the owner only sees once the menu is open, and the orb read only
	// `attention_flags` — so a tick whose one output was a proposal changed nothing the owner
	// could see from the collapsed dock.
	pendingProposals = 0,
): DockIconStatus {
	if (item.status === 'generating') return { kind: 'generating' };
	if (item.status === 'failed') return { kind: 'failed' };
	// Unread first: anything the owner has not seen pulses, whatever kind it is. Not gated on
	// `active`, because a paused strategy keeps any request it already made and the owner
	// still needs a way to reach it.
	const open = openFlags(item);
	if (open.some((f) => !f.acknowledged_at)) return { kind: 'attention' };
	// Read but still owed. An owner ask goes calm, because a reminder that pulses for a week
	// reads as a fault. Every other open request keeps pulsing until it is answered.
	if (standingFlags(item).length > 0) return { kind: 'standing' };
	if (open.length > 0) return { kind: 'attention' };
	// A proposal is a question, so it keeps the orb asking until the owner answers it.
	if (pendingProposals > 0) return { kind: 'attention' };
	if (item.status === 'active') return { kind: 'active' };
	return { kind: 'ready' };
}

// Newest first. The server appends, so without this the newest ask renders last — the least
// visible position for the most urgent thing. A flag stored before `since` existed sorts last
// under a total order, never through an inconsistent comparator.
export function flagsNewestFirst(flags: StrategyListItem['attention_flags']) {
	return [...(flags ?? [])]
		.filter((f) => !isRetired(f))
		.sort((a, b) => (b.since ?? '').localeCompare(a.since ?? ''));
}
